import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { GenerateButton } from '@creanizer/ui'
import { Checkbox, Label } from '@creanizer/ui'
import type { CommunityInput, SavedWorldBuilding, SavedCommunity, SavedCharacter, SavedEnemy } from '@creanizer/types'
import type { CustomValue } from '@creanizer/utils'
import { StandardModeForm } from './StandardModeForm'
import { TemplateModeForm } from './TemplateModeForm'
import { IdeaModeForm } from './IdeaModeForm'
import { ReferenceModeForm } from './ReferenceModeForm'

interface CommunityFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
  onGenerate: () => void
  isGenerating: boolean
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
  savedWorlds?: SavedWorldBuilding[]
  savedCommunities?: SavedCommunity[]
  savedCommunityTypes?: CustomValue[]
  characters?: SavedCharacter[]
  enemies?: SavedEnemy[]
  addCustomValue?: (type: CustomValue['type'], value: string, label: string, generatorType?: CustomValue['generatorType']) => Promise<void>
  useCustomValue?: (id: string) => Promise<void>
}

export function CommunityForm({
  input,
  onChange,
  onGenerate,
  isGenerating,
  useStreaming = false,
  onStreamingChange,
  savedWorlds = [],
  savedCommunities = [],
  savedCommunityTypes = [],
  characters = [],
  enemies = [],
  addCustomValue,
  useCustomValue,
}: CommunityFormProps) {
  // モード変更時のハンドラ - 他のモードの入力をクリア
  const handleModeChange = (newMode: string) => {
    if (newMode === 'standard') {
      onChange({
        mode: 'standard',
        name: '',
        communityType: 'town',
        scale: 'medium',
        population: undefined,
        wealthLevel: undefined,
        uniqueFeatures: [],
        problems: [],
        secrets: [],
        detailLevel: input.detailLevel || 5,
        creativityLevel: input.creativityLevel || 7,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'template') {
      onChange({
        mode: 'template',
        templateType: undefined,
        communityType: 'town',
        scale: 'medium',
        detailLevel: input.detailLevel || 5,
        creativityLevel: input.creativityLevel || 7,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'idea') {
      onChange({
        mode: 'idea',
        ideaText: '',
        communityType: 'town',
        scale: 'medium',
        detailLevel: input.detailLevel || 5,
        creativityLevel: input.creativityLevel || 7,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'reference') {
      onChange({
        mode: 'reference',
        references: {},
        referenceContext: '',
        additionalRequirements: '',
        communityType: 'town',
        scale: 'medium',
        detailLevel: input.detailLevel || 5,
        creativityLevel: input.creativityLevel || 7,
        useProMode: input.useProMode,
      })
    }
  }
  
  return (
    <div className="space-y-6">
      <Tabs value={input.mode} onValueChange={handleModeChange}>
        <TabsList className="grid w-full grid-cols-4 gap-1 h-auto">
          <TabsTrigger value="standard" className="text-xs sm:text-sm">カスタム</TabsTrigger>
          <TabsTrigger value="template" className="text-xs sm:text-sm">テンプレート</TabsTrigger>
          <TabsTrigger value="idea" className="text-xs sm:text-sm">アイディア</TabsTrigger>
          <TabsTrigger value="reference" className="text-xs sm:text-sm">参照</TabsTrigger>
        </TabsList>
        
        <TabsContent value="standard" className="space-y-4">
          <StandardModeForm
            input={input}
            onChange={onChange}
            savedCommunityTypes={savedCommunityTypes}
            savedWorlds={savedWorlds}
            savedCommunities={savedCommunities}
            characters={characters}
            enemies={enemies}
            addCustomValue={addCustomValue}
            useCustomValue={useCustomValue}
          />
        </TabsContent>
        
        <TabsContent value="template" className="space-y-4">
          <TemplateModeForm
            input={input}
            onChange={onChange}
            savedWorlds={savedWorlds}
            savedCommunities={savedCommunities}
            characters={characters}
            enemies={enemies}
          />
        </TabsContent>
        
        <TabsContent value="idea" className="space-y-4">
          <IdeaModeForm
            input={input}
            onChange={onChange}
            savedWorlds={savedWorlds}
            savedCommunities={savedCommunities}
            characters={characters}
            enemies={enemies}
          />
        </TabsContent>
        
        <TabsContent value="reference" className="space-y-4">
          <ReferenceModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
      </Tabs>
      
      {/* 共通の生成オプション */}
      <div className="space-y-4 border-t pt-4">
        <h3 className="text-sm font-semibold">生成オプション</h3>
        
        {/* 詳細度 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>詳細度</Label>
            <span className="text-sm text-gray-500">{input.detailLevel}/10</span>
          </div>
          <input
            type="range"
            min={1}
            max={10}
            value={input.detailLevel}
            onChange={(e) => onChange({ ...input, detailLevel: parseInt(e.target.value) })}
            className="w-full"
          />
          <p className="text-xs text-gray-500">
            詳細度が高いほど、より多くの情報が生成されます
          </p>
        </div>
        
        {/* 創造性レベル */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>創造性レベル</Label>
            <span className="text-sm text-gray-500">{input.creativityLevel || 7}/10</span>
          </div>
          <input
            type="range"
            min={1}
            max={10}
            value={input.creativityLevel || 7}
            onChange={(e) => onChange({ ...input, creativityLevel: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>
        
        {/* 詳細オプション */}
        {/* プロモード */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="pro-mode"
            checked={input.useProMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useProMode: checked === true })}
          />
          <Label htmlFor="pro-mode" className="cursor-pointer">プロモード（Gemini 2.5 Pro使用）</Label>
        </div>

        {/* Gemini 3 Flash モード */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="gemini3-flash-mode"
            checked={input.useGemini3FlashMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: checked === true })}
          />
          <Label htmlFor="gemini3-flash-mode" className="cursor-pointer">Gemini 3 Flash モード（試験版）</Label>
        </div>

        {/* ストリーミング */}
        {onStreamingChange && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="streaming"
              checked={useStreaming}
              onCheckedChange={onStreamingChange}
            />
            <Label htmlFor="streaming" className="cursor-pointer">ストリーミング生成を使用</Label>
          </div>
        )}
      </div>
      
      <GenerateButton onClick={onGenerate} isLoading={isGenerating}>
        共同体を生成
      </GenerateButton>
    </div>
  )
}